import React, {memo, useCallback} from "react";
import {QuestionComponentProps} from "../../domain";
import "../styles/SurveyDesign.css";

// Usar memo para prevenir re-renderizados innecesarios
const QuestionComponent: React.FC<QuestionComponentProps> = memo(
    ({question, answer, onAnswer, scale, isUnanswered}) => {
        // Usar useCallback para memoizar el manejador de eventos
        const handleChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                if (question.type === "boolean") {
                    // Para preguntas booleanas, convertir string 'true'/'false' a boolean
                    const boolValue = event.target.value === "true";
                    onAnswer(question.id, boolValue);
                } else {
                    // Para preguntas de escala, pasar el valor numérico
                    const numValue = parseInt(event.target.value, 10);

                    // Asegurarnos de que el valor no sea NaN
                    if (isNaN(numValue)) {
                        console.error(
                            `Invalid value for ${question.id}:`,
                            event.target.value
                        );
                        return;
                    }

                    onAnswer(question.id, numValue);
                }
            },
            [question.id, question.type, onAnswer]
        );

        return (
            <div
                className={`${
                    question.type === "boolean" ? "question-container-i" : "question-container"
                } ${isUnanswered ? "unanswered" : ""}`}
            >
                <div className="question">
                    {question.text}
                    {question.inverted}
                </div>
                {question.type === "boolean" ? (
                    <div className="options boolean-options">
                        <div className="option">
                            <input
                                type="radio"
                                id={`${question.id}-true`}
                                name={question.id}
                                value="true"
                                checked={answer === true}
                                onChange={handleChange}
                            />
                            <label htmlFor={`${question.id}-true`}>Sí</label>
                        </div>
                        <div className="option">
                            <input
                                type="radio"
                                id={`${question.id}-false`}
                                name={question.id}
                                value="false"
                                checked={answer === false}
                                onChange={handleChange}
                            />
                            <label htmlFor={`${question.id}-false`}>No</label>
                        </div>
                    </div>
                ) : (
                    <div className="scale-options">
                        {scale &&
                            Object.entries(scale).map(([key, value]) => {
                                return (
                                    <div className="option" key={key}>
                                        <input
                                            type="radio"
                                            id={`${question.id}-${value}`}
                                            name={question.id}
                                            value={value}
                                            checked={answer === value}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor={`${question.id}-${value}`}>{key}</label>
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
        );
    },
    (prevProps, nextProps) => {
        // Función de comparación personalizada para prevenir re-renderizados innecesarios
        // Solo se re-renderiza si la respuesta ha cambiado para esta pregunta específica
        return (
            prevProps.answer === nextProps.answer &&
            prevProps.question.id === nextProps.question.id  &&
            prevProps.isUnanswered === nextProps.isUnanswered
        );
    }
);

export default QuestionComponent;
